import { BaseScreen } from '@goodboydigital/astro';
import gsap from 'gsap';
import { Container, Point, Sprite, Texture } from 'pixi.js';

import { App, Shared } from '../App';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import LocalisedText from '../ui/LocalisedText';
import { StrUtils } from '../utils/StrUtils';
import { LetterSelector } from './../ui/LetterSelector';
import { Panel } from './../ui/Panel';

export class ChangeNameScreen extends BaseScreen
{
    public app: App;
    public manifests: string[] = [];
    public bg!: Sprite;
    public title!: LocalisedText;
    public confirmText!: LocalisedText;
    public backButton!: NineSliceButton;
    public settingsPanel!: Panel;
    public container!: Container;

    public firstCharSelect!: LetterSelector;
    public secondCharSelect!: LetterSelector;
    public thirdCharSelect!: LetterSelector;
    public _firstTime: boolean;
    public _backToHome: boolean;
    public _proceedToHome: boolean;
    
    constructor(app: App)
    {
        super(app);
        this.app = app;
        this._firstTime = false;
        this._backToHome = false;
        this._proceedToHome = false;
    }

    async init(): Promise<void>
    {
        const btnWidth = 300;
        const btnHeight = 85;
        const btnFontSize = 32;
        const btnFontStrokeThickness = 6;
        
        this.bg = Sprite.from(Texture.WHITE);
        this.bg.alpha = 0.1;
        this.bg.width = this.bg.height = 9999;
        this.bg.anchor.set(0.5);
        this.view.addChild(this.bg);

        this.container = new Container();
        this.view.addChild(this.container);

        this.settingsPanel = new Panel({
            bg: 'panel-license',
            // w: 720,
            w: 820,
            h: 466,
        });
        this.container.addChild(this.settingsPanel.view);

        this.mountDecoration();

        this.title = new LocalisedText('changeNameScreen_title', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 56,
            maxWidth: 650,
        });
        this.title.anchor.set(0.5);
        this.settingsPanel.view.addChild(this.title);
        this.title.y = (-this.settingsPanel.bg.height / 2) + 80;

        const confirmIcon = new Sprite();

        this.confirmText = new LocalisedText('changeNameScreen_confirm', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: btnFontStrokeThickness,
            lineJoin: 'round',
            align: 'center',
            fontSize: btnFontSize,
            maxWidth: 250,
        });

        this.confirmText.anchor.set(0.5);
        confirmIcon.addChild(this.confirmText);

        const confirmButton = new NineSliceButton({
            icon: confirmIcon,
            bgAsset: 'button-ninesliced-magenta',
            w: btnWidth,
            h: btnHeight,
        });

        confirmButton.view.y = (this.settingsPanel.bg.height / 2) + 55;

        this.settingsPanel.view.addChild(confirmButton.view);
        confirmButton.onPress.connect(() =>
        {
            Shared.settings.hasPlayerSetName = true;
            Shared.settings.playerName = this.firstCharSelect.text
                                                + this.secondCharSelect.text
                                                + this.thirdCharSelect.text;

            if (Shared.game) Shared.game.hud.nicknameWidget.refresh();
            this.toNextScreen();
        });

        const backIcon = Sprite.from(Shared.assets.getTexture('icon-back-arrow'));

        this.firstCharSelect = new LetterSelector();
        this.firstCharSelect.view.position.set(-160, 40);
        this.settingsPanel.view.addChild(this.firstCharSelect.view);

        this.secondCharSelect = new LetterSelector();
        this.secondCharSelect.view.position.set(0, 40);
        this.settingsPanel.view.addChild(this.secondCharSelect.view);

        this.thirdCharSelect = new LetterSelector();
        this.thirdCharSelect.view.position.set(160, 40);
        this.settingsPanel.view.addChild(this.thirdCharSelect.view);

        this.backButton = new NineSliceButton({
            icon: backIcon,
            accessibleTitle: 'Back',
            w: backIcon.width * 1.5,
            h: backIcon.height * 1.5,
        });
        this.view.addChild(this.backButton.view);
        this.backButton.onPress.connect(() => this.toPrevScreen());
    }

    private mountDecoration(): void
    {
        const wheel = new Sprite(Shared.assets.getTexture('license-wheel'));

        wheel.anchor.set(0.5);
        wheel.x = (this.settingsPanel.bg.width / 2) - 138;
        wheel.y = 50;
        this.settingsPanel.view.addChild(wheel);

        const track = new Sprite(Shared.assets.getTexture('tiretrack'));

        track.anchor.set(0.5);
        track.x = -(this.settingsPanel.bg.width / 2) + 140;
        track.y = 0;
        this.settingsPanel.view.addChild(track);

        const lanyard = new Sprite(Shared.assets.getTexture('license-lanyard'));

        lanyard.anchor.set(0.5, 1);
        lanyard.y = (-this.settingsPanel.bg.height / 2) + 45;
        this.settingsPanel.view.addChild(lanyard);

        const vehicles = new Sprite(Shared.assets.getTexture('license-vehicles'));

        vehicles.anchor.set(0.5);
        vehicles.x = (this.settingsPanel.bg.width / 2) - 100;
        vehicles.y = -(this.settingsPanel.bg.height / 2) + 40;
        this.settingsPanel.view.addChild(vehicles);
    }

    async prepare(screenData: Record<string, any>): Promise<void>
    {
        this._firstTime = screenData.firstTime ?? false;
        this._backToHome = screenData.backToHome ?? false;
        this._proceedToHome = screenData.proceedToHome ?? false;
    }
    
    public update(delta = 1): void
    {
        this.app.clouds?.update(delta);
    }

    show(): void
    {
        this.resize(this.width, this.height);

        // If first time, set a random user name
        if (this._firstTime)
        {
            Shared.settings.playerName = StrUtils.randomNickname();
        }

        const currentName = Shared.settings.playerName;

        this.firstCharSelect.text = currentName.charAt(0);
        this.secondCharSelect.text = currentName.charAt(1);
        this.thirdCharSelect.text = currentName.charAt(2);

        this.view.visible = true;
        this.backButton.view.visible = !this._firstTime;

        gsap.killTweensOf(this.container);
        this.container.y = -200;
        this.container.rotation = -Math.PI * 0.3;
        gsap.to(this.container, { rotation: 0, duration: 2, ease: 'elastic.out' });
        Shared.game.soundSystem.playSfx('windmill');
    }

    async hide(): Promise<void>
    {
        gsap.killTweensOf(this.container);
        await gsap.to(this.container, { y: -1000, duration: 0.5, ease: 'back.in' });
    }

    hidden(): void
    {
        this.view.visible = false;
    }

    resize(w: number, h: number): void
    {
        super.resize(w, h);
        this.view.x = (this.width - this.app.width) * 0.5;
        this.view.y = (this.height - this.app.height) * 0.5;
        this.bg.position.copyFrom(this.app.stageCenter as Point);

        this.container.x = this.app.stageCenter.x;
        this.container.y = -200;
        this.settingsPanel.view.y = this.app.stageCenter.y + 200;

        this.backButton.view.position.set(
            this.app.stageLeft + 90,
            this.app.stageTop + 90,
        );
        
        this.app.clouds?.resize(w, h);
    }

    private toPrevScreen(): void
    {
        if (this._backToHome)
        {
            this.app.controller.toHome();
        }
        else if (!this._firstTime)
        {
            this.app.controller.toSettings();
        }
        else
        {
            this.app.controller.toLanguageSelect(this._firstTime);
        }
    }

    private toNextScreen(): void
    {
        this.app.controller.toFlagSelect(this._firstTime, this._backToHome, this._proceedToHome);
    }
}
